import { supabase } from '@/integrations/supabase/client';
import type { Idea } from './types';

interface FullStructure {
  title: string;
  one_liner: string;
  problem: string;
  audience: string;
  proposed_solution: string;
  differentiator: string;
  keywords_tags: string[];
  business_name_options: string[];
  tagline_options: string[];
  business_models: string[];
  mvp_outline: string[];
  first_experiments: string[];
  risks_unknowns: string[];
  domain_suggestions: string[];
}

interface ExpansionResult {
  [key: string]: string | string[];
}

// Sections that can be unlocked through watering, in order of expansion
const EXPANSION_ORDER: (keyof Idea)[] = [
  'audience',
  'proposed_solution',
  'differentiator',
  'keywords_tags',
  'business_name_options',
  'tagline_options',
  'business_models',
  'mvp_outline',
  'first_experiments',
  'risks_unknowns',
  'domain_suggestions',
];

/**
 * Full structuring on plant - all fields at once
 * Calls AI via edge function
 */
export async function structureIdeaFull(rawInput: string): Promise<FullStructure> {
  const { data, error } = await supabase.functions.invoke('structure-idea', {
    body: { action: 'initial', rawInput }
  });

  if (error) {
    console.error('AI structuring error:', error);
    throw new Error(error.message || 'Failed to structure idea');
  }

  if (data.error) {
    throw new Error(data.error);
  }

  return data;
}

/**
 * Expand idea on water - generates 1-2 new sections based on current state
 */
export async function expandIdeaOnWater(idea: Idea): Promise<ExpansionResult> {
  const { data, error } = await supabase.functions.invoke('structure-idea', {
    body: { action: 'expand', idea }
  });

  if (error) {
    console.error('AI expansion error:', error);
    throw new Error(error.message || 'Failed to expand idea');
  }

  if (data.error) {
    throw new Error(data.error);
  }

  return data;
}

/**
 * Generate a concise AI summary of a reflection
 */
export async function summarizeReflection(
  reflection: string, 
  ideaTitle?: string | null, 
  ideaOneLiner?: string | null
): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke('structure-idea', {
      body: { 
        action: 'summarize_reflection', 
        reflection,
        ideaTitle: ideaTitle || 'Untitled',
        ideaOneLiner: ideaOneLiner || ''
      }
    });

    if (error) {
      console.error('AI summary error:', error);
      return null;
    }

    return data?.summary || null;
  } catch (err) {
    console.error('AI summary failed:', err);
    return null;
  }
}

/**
 * Check if an idea has room for AI expansion
 */
export function canExpandIdea(idea: Idea): boolean {
  return EXPANSION_ORDER.some(field => {
    const value = idea[field];
    if (Array.isArray(value)) return value.length === 0;
    return !value;
  });
}
